// react
import { useCallback, useMemo, useState } from 'react';

// mui
import { Button } from '@mui/material';

// components
import { CopyText } from '@karehero/llama/components/atoms/CopyText/CopyText';
import { Dialog } from '@karehero/llama/components/atoms/Dialog/Dialog';
import { EditorFields } from '@karehero/llama/components/organisms/EditorFields/EditorFields';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { Label } from '@karehero/llama/components/atoms/Label/Label';
import AdminTablePage from '@karehero/llama/pwa/templates/AdminTablePage/AdminTablePage';
import DataTable, {
  GridColDef,
} from '@karehero/llama/pwa/organisms/DataTable/DataTable';

// store
import {
  useCreateServiceAccountMutation,
  useDeleteServiceAccountMutation,
  useGetAllServiceAccountQuery,
} from 'store/api/serviceAccount';
import {
  FieldType,
  ServiceAccount,
  ServiceAccountWithToken,
} from '@karehero/models';
import { newToast } from 'store/slices/uiSlice';
import { dispatch } from 'store';

// columns
const columns: GridColDef[] = [
  { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
  { field: 'role', headerName: 'Role', flex: 1, minWidth: 150 },
];

type ServiceAccountDialogs = 'create' | 'created' | 'confirmDelete' | undefined;

export const ServiceAccountTable = () => {
  // hooks
  const { data: serviceAccounts, refetch: refetchServiceAccounts } =
    useGetAllServiceAccountQuery();
  const [draftServiceAccount, setDraftServiceAccount] =
    useState<ServiceAccount>({ name: '', role: '' });
  const [serviceAccount, setServiceAccount] =
    useState<ServiceAccountWithToken | null>(null);
  const [serviceAccountDialogs, setServiceAccountDialogs] = useState<
    ServiceAccountDialogs | undefined
  >(undefined);
  const [deleteServiceAccountID, setDeleteServiceAccountID] = useState<
    string | undefined
  >(undefined);

  const [createServiceAccount] = useCreateServiceAccountMutation();
  const [deleteServiceAccount] = useDeleteServiceAccountMutation();

  const createServiceAccountCallback = useCallback(async () => {
    if (!draftServiceAccount) return;

    const res = await createServiceAccount(draftServiceAccount);
    if ('data' in res) {
      setServiceAccount(res.data ?? null);
      setServiceAccountDialogs('created');
    }

    refetchServiceAccounts();
  }, [
    createServiceAccount,
    refetchServiceAccounts,
    draftServiceAccount,
    serviceAccounts,
    setServiceAccountDialogs,
  ]);

  // memos
  const rows = useMemo(() => {
    if (!serviceAccounts) return [];
    return serviceAccounts.map((serviceAccount) => ({
      ...serviceAccount,
    }));
  }, [serviceAccounts]);

  const showDeleteConfirmation = useCallback(
    (id: string | undefined) => {
      if (!id) return;

      setDeleteServiceAccountID(id);
      setServiceAccountDialogs('confirmDelete');
    },
    [setDeleteServiceAccountID, setServiceAccountDialogs],
  );

  const handleDeleteAccount = useCallback(
    async (id: string | undefined) => {
      if (!id) return;

      await deleteServiceAccount({ id });
      dispatch(
        newToast({
          title: 'Service Account Deleted',
          description: 'Service account successfully deleted',
          variant: 'success',
        }),
      );
      refetchServiceAccounts();
      setServiceAccountDialogs(undefined);
      setDeleteServiceAccountID(undefined);
    },
    [deleteServiceAccount, dispatch, refetchServiceAccounts],
  );

  return (
    <AdminTablePage title='Service Accounts'>
      <Dialog
        variant='dialog'
        isOpen={serviceAccountDialogs === 'create'}
        setIsOpen={() => setServiceAccountDialogs('create')}
        zIndex={1}
        title='Create Service Account'
      >
        <EditorFields
          fields={{
            name: {
              label: 'Name',
              field: {
                type: FieldType.Text,
              },
            },
            role: {
              label: 'Role',
              field: {
                type: FieldType.Text,
              },
            },
          }}
          value={draftServiceAccount}
          onChange={setDraftServiceAccount}
        />

        <Button variant='contained' onClick={createServiceAccountCallback}>
          Create Service Account
        </Button>
      </Dialog>

      <Dialog
        variant='dialog'
        title='Service Account Created'
        isOpen={serviceAccountDialogs === 'created'}
        setIsOpen={() => setServiceAccountDialogs(undefined)}
        zIndex={1}
      >
        <Label
          text={'Name: ' + serviceAccount?.serviceAccount.name}
          id='name'
        />
        <Label
          text={'Role: ' + serviceAccount?.serviceAccount.role}
          id='role'
        />
        <Label text={'Token: '} id='token' />
        <CopyText
          value={serviceAccount?.token}
          onCopy={() => {
            navigator.clipboard.writeText(serviceAccount?.token || '');
          }}
        />
      </Dialog>

      <Dialog
        variant='dialog'
        title='Are you sure you want to delete this service account?'
        isOpen={serviceAccountDialogs === 'confirmDelete'}
        setIsOpen={() => setServiceAccountDialogs(undefined)}
        zIndex={1}
      >
        <Button
          variant='contained'
          onClick={() => handleDeleteAccount(deleteServiceAccountID)}
        >
          Delete Service Account
        </Button>
        <Button
          variant='outlined'
          onClick={() => setServiceAccountDialogs(undefined)}
        >
          Cancel
        </Button>
      </Dialog>

      <DataTable
        columns={columns}
        rows={rows}
        rowsPerPage={10}
        toolbarActions={
          <Button
            variant='contained'
            size='small'
            onClick={() => {
              setServiceAccountDialogs('create');
              setServiceAccount(null);
            }}
            startIcon={<Icon icon='plus' size='sm' />}
          >
            New Service Account
          </Button>
        }
        actions={[
          {
            label: 'Delete',
            icon: 'trash',
            onPress: (sa: ServiceAccount) => {
              showDeleteConfirmation(sa.id);
            },
          },
        ]}
      />
    </AdminTablePage>
  );
};

export default ServiceAccountTable;
